import { useEffect, useRef, useState } from 'react';
import { Download, FileSearch, ShieldAlert } from 'lucide-react';
import { chamarApi, ErroApi } from '../../nucleo/auth/cliente-api';
import { PageHeader } from '../../ui/PageHeader';
import { Alert, AlertDescription } from '../../ui/base/alert';
import { Button } from '../../ui/base/button';
import { Input } from '../../ui/base/input';
import { Label } from '../../ui/base/label';
import { EmptyState } from '../../ui/EmptyState';
const NS = '/bff/v1/j12-s04-auditar-trilha';
type Documento = { id: string; codigo_verificacao: string; estado: string };
type Transicao = {
  tipo: string;
  autor: string;
  motivo: string | null;
  valor: string | number | null;
  hash: string;
};
type Cadeia = {
  cadeiaHash: string;
  documento: { codigo_verificacao: string; sha256: string; resultado_hash: string };
  transicoes: Transicao[];
};
type S = {
  titulo: string;
  descricao: string;
  documentos: Documento[];
  cadeia: Cadeia | null;
  acessos: Array<{ id: string }>;
  achados: Array<{ id: string }>;
  alertas: Array<{ mensagem: string }>;
  permissoes: { registrarAchado: boolean };
  download?: { nome: string; mimeType: string; conteudoBase64: string } | null;
};
type E = { state: S };
export function AuditarTrilhaPage() {
  const [s, setS] = useState<S | null>(null),
    [doc, setDoc] = useState(''),
    [titulo, setTitulo] = useState(''),
    [descricao, setDescricao] = useState(''),
    [erro, setErro] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    chaves = useRef({
      leitura: crypto.randomUUID(),
      exportacao: crypto.randomUUID(),
      achado: crypto.randomUUID(),
    });
  async function verbo(nome: string, corpo: Record<string, string>) {
    setBusy(true);
    setErro(null);
    try {
      const x = (await chamarApi<E>(`${NS}/verbs/${nome}`, { metodo: 'POST', corpo })).state;
      setS(x);
      if (x.download) {
        const a = document.createElement('a');
        a.href = `data:${x.download.mimeType};base64,${x.download.conteudoBase64}`;
        a.download = x.download.nome;
        a.click();
      }
    } catch (x) {
      setErro(x instanceof ErroApi ? x.aviso : 'Não foi possível concluir.');
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void chamarApi<E>(`${NS}/state`)
      .then((x) => {
        setS(x.state);
        setDoc(x.state.documentos[0]?.id ?? '');
      })
      .catch(() => setErro('Não foi possível carregar a auditoria.'));
  }, []);
  if (!s)
    return (
      <EmptyState
        variant={erro ? 'error' : 'default'}
        title={erro ?? 'Carregando auditoria…'}
        description="Aguarde."
      />
    );
  return (
    <main className="min-w-0 max-w-[calc(100vw-2rem)] space-y-5 overflow-hidden">
      <PageHeader title={s.titulo} description={s.descricao} />
      {[...s.alertas.map((a) => a.mensagem), ...(erro ? [erro] : [])].map((m, i) => (
        <Alert key={i}>
          <AlertDescription>{m}</AlertDescription>
        </Alert>
      ))}
      <section className="grid gap-3 rounded-md border p-4">
        <Label htmlFor="documento-auditoria">Documento verificável</Label>
        <select
          id="documento-auditoria"
          className="h-10 min-w-0 rounded-md border bg-background px-3"
          value={doc}
          onChange={(x) => setDoc(x.target.value)}
        >
          {s.documentos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.codigo_verificacao} · {d.estado}
            </option>
          ))}
        </select>
        <Button
          disabled={busy || !doc}
          onClick={() =>
            void verbo('consultar_cadeia', {
              documentoId: doc,
              chaveIdempotencia: chaves.current.leitura,
            })
          }
        >
          <FileSearch className="mr-2 h-4 w-4" />
          Percorrer cadeia
        </Button>
      </section>
      {s.cadeia && (
        <>
          <section className="rounded-md border p-4">
            <h2 className="font-semibold">Cadeia {s.cadeia.cadeiaHash}</h2>
            <p className="mt-2 break-all text-sm">
              Documento {s.cadeia.documento.codigo_verificacao} · hash {s.cadeia.documento.sha256} ·
              resultado {s.cadeia.documento.resultado_hash}
            </p>
            <ol className="mt-3 space-y-2" aria-label="Transições imutáveis">
              {s.cadeia.transicoes.map((t, i) => (
                <li key={`${t.tipo}-${i}`} className="rounded border p-2 text-sm">
                  <strong>{t.tipo}</strong> · autor {t.autor}
                  <br />
                  Motivo {t.motivo ?? 'Não aplicável'} · valor {t.valor ?? 'Não aplicável'}
                  <br />
                  <span className="break-all">Hash {t.hash}</span>
                </li>
              ))}
            </ol>
            <Button
              className="mt-3"
              variant="outline"
              disabled={busy}
              onClick={() =>
                void verbo('exportar_auditoria', {
                  documentoId: doc,
                  chaveIdempotencia: chaves.current.exportacao,
                })
              }
            >
              <Download className="mr-2 h-4 w-4" />
              Exportar auditoria e registrar
            </Button>
          </section>
          {s.permissoes.registrarAchado && (
            <section className="grid gap-3 rounded-md border p-4">
              <h2 className="font-semibold">
                <ShieldAlert className="mr-2 inline h-5 w-5" />
                Abrir achado separado
              </h2>
              <Label htmlFor="titulo-achado">Título</Label>
              <Input
                id="titulo-achado"
                value={titulo}
                onChange={(x) => setTitulo(x.target.value)}
              />
              <Label htmlFor="descricao-achado">Descrição</Label>
              <Input
                id="descricao-achado"
                value={descricao}
                onChange={(x) => setDescricao(x.target.value)}
              />
              <Button
                disabled={busy || titulo.trim().length < 5 || descricao.trim().length < 20}
                onClick={() =>
                  void verbo('registrar_achado', {
                    documentoId: doc,
                    chaveIdempotencia: chaves.current.achado,
                    titulo,
                    descricao,
                  })
                }
              >
                Registrar achado sem alterar evidência
              </Button>
            </section>
          )}
        </>
      )}
      <section>
        <h2 className="font-semibold">Leituras, exportações e achados registrados</h2>
        <p className="text-sm">
          {s.acessos.length} acesso(s) · {s.achados.length} achado(s). Edição e exclusão não estão
          disponíveis.
        </p>
      </section>
    </main>
  );
}
